'use client';

import { DeleteOutlined } from '@mui/icons-material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { Box, Button, FormControlLabel, FormGroup, Grid, MenuItem, Switch, TextField, Tooltip } from '@mui/material';
import {
	DataGridPremium,
	GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
	GridActionsCellItem,
	GridColDef,
	GridRenderEditCellParams,
	GridRowId,
} from '@mui/x-data-grid-premium';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker, LocalizationProvider, PickersLayout, pickersLayoutClasses } from '@mui/x-date-pickers-pro';
import { gql, useLazyQuery } from '@parkchirp/api-client';
import { adaptGridValueCallback } from '@parkchirp/ui-library';
import { cloneDeep } from 'lodash';
import React, { type Dispatch, type FC, type SetStateAction, useCallback, useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import type { SetShowSnackbar } from '../../../types';
import { Dayjs, dayjs, isOwnerPortal, normalizeState, provinceFieldOptions, stateFieldOptions } from '../../../utils';
import { MakeAutocomplete, ModelAutocomplete } from '../../vehicles';
import { getVehicleLimitState, type Vehicle, validateVehicleLimit } from './vehicleDetailConfig';
import { VehicleHistoryPanel } from './vehicleHistoryPanel';

const GET_PARKER_VEHICLES = gql`
    query getMonthlyParkerVehicles(
        $parkerId: String!,
        $tenantSubscriptionId: String
    ) {
        getMonthlyParkerVehicles(
            parkerId: $parkerId,
            tenantSubscriptionId: $tenantSubscriptionId
        ) {
            id
            make
            model
            license
            state
            color
            decal
            startDate
            endDate
			year
			surchargeFeeNames
            vehicleHistory {
                id
                color
                licensePlate
                licenseState
                createdByUser {
                    email
                }
                activeDate
                inactiveDate
                decal
            }
        }
    }
`;

type MonthlyParkerVehicleData = {
	getMonthlyParkerVehicles: {
		id: string;
		make: string;
		model: string;
		license: string;
		state: string;
	}[];
};

const currentYear = new Date().getFullYear();
const yearOptions = Array.from({ length: currentYear + 1 - 1979 }, (_, i) => currentYear + 1 - i);
interface VehiclesProps {
	parkerId: string;
	newVehicles: Vehicle[];
	setNewVehicles: (vehicles: any) => any[] | void;
	onVehiclesLoaded?: (vehicles: any[]) => void;
	setHasUnsavedChanges: (hasUnsavedChanges: boolean) => void;
	isEditMode: boolean;
	tenantSubscriptionId?: string;
	facilityIntegration: string;
	facilityId: string;
	setShowSnackbar: SetShowSnackbar;
	newBillingStartDate: Dayjs;
	setVehicleHistoryChanged: Dispatch<SetStateAction<boolean>>;
	maxActiveVehicles: number;
	refreshKey?: number;
}

export const VehiclesDetail: FC<VehiclesProps> = ({
	parkerId,
	newVehicles,
	setNewVehicles,
	onVehiclesLoaded,
	setHasUnsavedChanges,
	isEditMode,
	tenantSubscriptionId,
	facilityIntegration,
	facilityId,
	setShowSnackbar,
	setVehicleHistoryChanged,
	maxActiveVehicles = 100,
	refreshKey = 0,
}) => {
	const [getMonthlyParkerVehicles, { data: vehicleData, loading: vehicleLoading }] =
		useLazyQuery<MonthlyParkerVehicleData>(GET_PARKER_VEHICLES);
	const [showAllRows, setShowAllRows] = useState<boolean>(false);

	const today = dayjs.utc();
	const {
		canAddMoreVehicles,
		defaultNewVehicleStartDate,
		maxNewVehicleEndDate,
		reason: addVehicleDisabledReason,
	} = getVehicleLimitState({
		vehicles: newVehicles,
		maxActiveVehicles,
	});

	const vehiclesToShow = showAllRows
		? newVehicles
		: newVehicles.filter((row) => {
				const startOk = dayjs.utc(row.startDate).isSameOrBefore(today, 'day');
				const endOk = row.endDate ? dayjs.utc(row.endDate).isSameOrAfter(today, 'day') : true;
				return startOk && endOk;
			});

	useEffect(() => {
		if (parkerId) {
			getMonthlyParkerVehicles({
				variables: {
					parkerId,
					tenantSubscriptionId,
				},
				context: {
					apiVersion: 'v2',
					appName: process.env.NEXT_PUBLIC_APP_NAME,
				},
				fetchPolicy: 'network-only',
			});
		}
	}, [getMonthlyParkerVehicles, parkerId, refreshKey, tenantSubscriptionId]);

	useEffect(() => {
		if (vehicleData?.getMonthlyParkerVehicles) {
			const vehicles = cloneDeep(vehicleData.getMonthlyParkerVehicles);
			const todayStart = dayjs.utc().startOf('day');
			const mappedVehicles = vehicles.map((v: any) => {
				if (!v.license) {
					v.needsLicense = true;
				}
				v.isPastEndDateFromDb = !!v.endDate && dayjs.utc(v.endDate).isBefore(todayStart);
				v.hasEndDate = !!v.endDate;
				v.yearFromDb = v.year;
				return v;
			});
			setNewVehicles(mappedVehicles);
			onVehiclesLoaded?.(mappedVehicles);
		}
	}, [onVehiclesLoaded, setNewVehicles, vehicleData]);

	const handleAddVehicle = useCallback(() => {
		const minStartDate = defaultNewVehicleStartDate.startOf('day').toDate();
		const maxEndDate = maxNewVehicleEndDate ? maxNewVehicleEndDate.startOf('day').toDate() : null;

		setNewVehicles((prevVehicles) => [
			...prevVehicles,
			{
				id: uuidv4(),
				isNew: true,
				make: null,
				model: null,
				license: '',
				state: '',
				startDate: minStartDate,
				minStartDate,
				endDate: maxEndDate,
				maxEndDate,
			},
		]);
	}, [setNewVehicles, defaultNewVehicleStartDate, maxNewVehicleEndDate]);

	const handleDeleteVehicle = useCallback(
		(id: GridRowId) => () => {
			setNewVehicles((prevVehicles) => prevVehicles.filter((row) => row.id !== id));
		},
		[setNewVehicles],
	);

	const handleKeyDown = useCallback((event) => {
		if (event.key === ' ') {
			event.stopPropagation();
		}
	}, []);

	const getDetailPanelContent = useCallback(
		({ row }) =>
			row.isNew ? null : (
				<VehicleHistoryPanel
					facilityId={facilityId}
					vehicleId={row.id}
					history={row.vehicleHistory}
					setShowSnackbar={setShowSnackbar}
					tenantSubscriptionId={tenantSubscriptionId}
					rowEndDate={row.endDate}
					isEditMode={isEditMode}
					setVehicleHistoryChanged={setVehicleHistoryChanged}
				/>
			),
		[facilityId, setShowSnackbar, tenantSubscriptionId, isEditMode],
	);

	const getDetailPanelHeight = useCallback(({ row }) => (row.isNew ? 0 : 'auto'), []);

	const detailToggleCol = {
		...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
		renderCell: (params) => {
			if (params.row.isNew) {
				return null;
			}
			return GRID_DETAIL_PANEL_TOGGLE_COL_DEF.renderCell?.(params) ?? null;
		},
	};

	const DateEditCell = ({ params, field }: { params: GridRenderEditCellParams; field: 'startDate' | 'endDate' }) => {
		const { row } = params;

		const value = row?.[field] ? dayjs.utc(row[field]).startOf('day') : null;

		const today = dayjs.utc().startOf('day');
		const yesterday = dayjs.utc().subtract(1, 'day').startOf('day');

		const startDateMin = row?.minStartDate
			? dayjs.utc(row.minStartDate).startOf('day')
			: defaultNewVehicleStartDate?.isValid()
				? defaultNewVehicleStartDate.startOf('day')
				: yesterday;

		const minDate =
			field === 'endDate'
				? row?.startDate
					? dayjs.utc(row.startDate).startOf('day').isAfter(today)
						? dayjs.utc(row.startDate).startOf('day')
						: yesterday
					: today
				: startDateMin;

		const maxDate =
			field === 'startDate'
				? row?.endDate
					? dayjs.utc(row.endDate).startOf('day')
					: undefined
				: row?.maxEndDate
					? dayjs.utc(row.maxEndDate).startOf('day')
					: undefined;

		const isDateDisabled = (date: Dayjs) => {
			const normalized = dayjs.utc(date).startOf('day');

			if (minDate?.isValid() && normalized.isBefore(minDate, 'day')) {
				return true;
			}

			if (maxDate?.isValid() && normalized.isAfter(maxDate, 'day')) {
				return true;
			}

			return false;
		};

		const getVehiclesAfterChange = (nextValue: Dayjs | null) =>
			newVehicles.map((vehicle) =>
				vehicle.id === row.id
					? {
							...vehicle,
							[field]: nextValue ? nextValue.startOf('day').toDate() : null,
						}
					: vehicle,
			);

		const canUseValue = (nextValue: Dayjs | null) => {
			if (!nextValue) {
				return true;
			}

			return !isDateDisabled(nextValue);
		};

		const canUseQuickActionValue = (nextValue: Dayjs | null) => {
			if (!canUseValue(nextValue)) {
				return false;
			}

			return validateVehicleLimit({
				vehicles: getVehiclesAfterChange(nextValue),
				maxActiveVehicles,
			}).isValid;
		};

		const getQuickActionReason = (nextValue: Dayjs | null) => {
			if (!canUseValue(nextValue)) {
				return 'This date is outside the allowed range.';
			}

			const validation = validateVehicleLimit({
				vehicles: getVehiclesAfterChange(nextValue),
				maxActiveVehicles,
			});

			return validation.reason || 'This change would exceed the facility max active vehicles limit.';
		};

		const setValue = (val: Dayjs | null) => {
			if (!canUseValue(val)) {
				return;
			}

			setHasUnsavedChanges(true);
			setNewVehicles((prevVehicles) =>
				prevVehicles.map((vehicle) =>
					vehicle.id === row.id
						? {
								...vehicle,
								[field]: val ? val.startOf('day').toDate() : null,
							}
						: vehicle,
				),
			);
		};

		const QuickActions = ({ onPick, onClear }: { onPick: (d: Dayjs) => void; onClear: () => void }) => {
			const yesterday = dayjs.utc().subtract(1, 'day');
			const today = dayjs.utc();
			const tomorrow = dayjs.utc().add(1, 'day');
			const nextWeek = dayjs.utc().add(1, 'week');

			const canPickYesterday = canUseQuickActionValue(yesterday);
			const canPickToday = canUseQuickActionValue(today);
			const canPickTomorrow = canUseQuickActionValue(tomorrow);
			const canPickNextWeek = canUseQuickActionValue(nextWeek);
			const canClear = field === 'endDate' ? canUseQuickActionValue(null) : true;

			return (
				<Box
					style={{
						display: 'flex',
						flexDirection: 'column',
						justifyContent: 'space-between',
						padding: '5px',
					}}
				>
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
						<Tooltip
							title={canPickYesterday ? '' : getQuickActionReason(yesterday)}
							disableHoverListener={canPickYesterday}
						>
							<span>
								<Button size='small' disabled={!canPickYesterday} onClick={() => onPick(yesterday)}>
									Yesterday
								</Button>
							</span>
						</Tooltip>

						<Tooltip
							title={canPickToday ? '' : getQuickActionReason(today)}
							disableHoverListener={canPickToday}
						>
							<span>
								<Button size='small' disabled={!canPickToday} onClick={() => onPick(today)}>
									Today
								</Button>
							</span>
						</Tooltip>

						<Tooltip
							title={canPickTomorrow ? '' : getQuickActionReason(tomorrow)}
							disableHoverListener={canPickTomorrow}
						>
							<span>
								<Button size='small' disabled={!canPickTomorrow} onClick={() => onPick(tomorrow)}>
									Tomorrow
								</Button>
							</span>
						</Tooltip>

						<Tooltip
							title={canPickNextWeek ? '' : getQuickActionReason(nextWeek)}
							disableHoverListener={canPickNextWeek}
						>
							<span>
								<Button size='small' disabled={!canPickNextWeek} onClick={() => onPick(nextWeek)}>
									Next Week
								</Button>
							</span>
						</Tooltip>
					</Box>

					<Tooltip title={canClear ? '' : getQuickActionReason(null)} disableHoverListener={canClear}>
						<span>
							<Button
								sx={{ backgroundColor: '#607d8b' }}
								size='small'
								onClick={onClear}
								disabled={!canClear}
							>
								Clear
							</Button>
						</span>
					</Tooltip>
				</Box>
			);
		};

		return (
			<LocalizationProvider dateAdapter={AdapterDayjs}>
				<DatePicker<Dayjs>
					timezone='UTC'
					value={value}
					minDate={minDate?.isValid() ? minDate : undefined}
					maxDate={maxDate?.isValid() ? maxDate : undefined}
					shouldDisableDate={isDateDisabled}
					onChange={setValue}
					format='MM/DD/YYYY'
					slots={{
						layout: (props) => (
							<Box sx={{ display: 'flex', gap: 1 }}>
								<QuickActions onPick={(d) => setValue(d)} onClear={() => setValue(null)} />
								<PickersLayout {...props} />
							</Box>
						),
					}}
					slotProps={{
						textField: {
							inputProps: { readOnly: true },
							onKeyDown: (e) => e.preventDefault(),
						},
						layout: {
							sx: {
								[`& .${pickersLayoutClasses.actionBar}`]: { display: 'none' },
							},
						},
					}}
				/>
			</LocalizationProvider>
		);
	};

	const vehicleColumns: GridColDef[] = [
		detailToggleCol,
		{
			field: 'make',
			headerName: 'Make',
			headerAlign: 'center' as const,
			cellClassName: 'tableCell',
			flex: 2,
			renderCell: (params) =>
				isEditMode && params.row.isNew ? (
					<MakeAutocomplete
						make={params.row.make}
						setMake={(make) => {
							setHasUnsavedChanges(true);
							params.row.make = make;
							setNewVehicles([...newVehicles]);
						}}
						setModel={(model) => {
							setHasUnsavedChanges(true);
							params.row.model = model;
							setNewVehicles([...newVehicles]);
						}}
						showLabel={false}
					/>
				) : (
					params.row.make || ''
				),
			resizable: false,
			sortable: false,
		},
		{
			field: 'model',
			headerName: 'Model',
			headerAlign: 'center' as const,
			cellClassName: 'tableCell',
			flex: 2,
			renderCell: (params) =>
				isEditMode && params.row.isNew ? (
					<ModelAutocomplete
						model={params.row.model}
						make={params.row.make}
						setModel={(model) => {
							setHasUnsavedChanges(true);
							params.row.model = model;
							setNewVehicles([...newVehicles]);
						}}
						showLabel={false}
					/>
				) : (
					params.row.model || ''
				),
			resizable: false,
			sortable: false,
		},
		{
			field: 'license',
			headerName: 'License',
			headerAlign: 'center' as const,
			cellClassName: 'tableCell',
			flex: 1,
			renderCell: (params) =>
				isEditMode && params.row.isNew ? (
					<TextField
						value={params.row.license || ''}
						onKeyDown={handleKeyDown}
						onChange={(e) => {
							if (params.row.isNew || params.row.needsLicense) {
								setHasUnsavedChanges(true);
								params.row.license = e.target.value;
								setNewVehicles([...newVehicles]);
							}
						}}
						inputProps={{
							'data-cy': 'license-input',
						}}
					/>
				) : (
					params.row.license || ''
				),
			resizable: false,
			sortable: false,
		},
		{
			field: 'year',
			headerName: 'Year',
			headerAlign: 'center' as const,
			cellClassName: 'tableCell',
			flex: 1.5,
			renderCell: (params) => {
				const { year, yearFromDb } = params.row;

				if (isEditMode && !yearFromDb) {
					return (
						<TextField
							select
							value={params.row.year || ''}
							onKeyDown={handleKeyDown}
							onChange={(e) => {
								setHasUnsavedChanges(true);
								params.row.year = Number(e.target.value);
								setNewVehicles([...newVehicles]);
							}}
							inputProps={{
								'data-cy': 'year-input',
							}}
							fullWidth
						>
							{yearOptions.map((year) => (
								<MenuItem key={year} value={year}>
									{year}
								</MenuItem>
							))}
						</TextField>
					);
				}

				return year || '';
			},
			resizable: false,
			sortable: false,
		},
		{
			field: 'state',
			headerName: 'State/Province',
			headerAlign: 'center' as const,
			cellClassName: 'tableCell',
			flex: 1,
			valueFormatter: adaptGridValueCallback(({ value }) => {
				return normalizeState(value) || '';
			}),
			renderCell: (params) => {
				if (isEditMode && params.row.isNew) {
					return (
						<TextField
							select
							value={params.row.state || ''}
							onKeyDown={handleKeyDown}
							onChange={(e) => {
								if (params.row.isNew) {
									setHasUnsavedChanges(true);
									params.row.state = e.target.value;
									setNewVehicles([...newVehicles]);
								}
							}}
							style={{ width: '100%' }}
							data-cy='state-select-menu'
						>
							{[...stateFieldOptions, ...provinceFieldOptions].map((option) => (
								<MenuItem key={option.value} value={option.value}>
									{option.value}
								</MenuItem>
							))}
						</TextField>
					);
				}

				return normalizeState(params.value) || '';
			},
			resizable: false,
			sortable: false,
		},
		{
			field: 'color',
			headerName: 'Color',
			headerAlign: 'center' as const,
			cellClassName: 'tableCell',
			flex: 1,
			renderCell: (params) =>
				isEditMode && params.row.isNew ? (
					<TextField
						value={params.row.color || ''}
						onKeyDown={handleKeyDown}
						onChange={(e) => {
							if (params.row.isNew) {
								setHasUnsavedChanges(true);
								params.row.color = e.target.value;
								setNewVehicles([...newVehicles]);
							}
						}}
						inputProps={{
							'data-cy': 'color-input',
						}}
					/>
				) : (
					params.row.color || ''
				),
			resizable: false,
			sortable: false,
		},
		{
			field: 'decal',
			headerName: 'Decal',
			headerAlign: 'center' as const,
			cellClassName: 'tableCell',
			flex: 1,
			renderCell: (params) =>
				isEditMode && params.row.isNew ? (
					<TextField
						value={params.row.decal || ''}
						onKeyDown={handleKeyDown}
						onChange={(e) => {
							if (params.row.isNew) {
								setHasUnsavedChanges(true);
								params.row.decal = e.target.value;
								setNewVehicles([...newVehicles]);
							}
						}}
						inputProps={{
							'data-cy': 'decal-input',
						}}
					/>
				) : (
					params.row.decal || ''
				),
			resizable: false,
			sortable: false,
		},
		{
			field: 'startDate',
			headerName: 'Start Date',
			headerAlign: 'center' as const,
			cellClassName: 'tableCell',
			flex: 2,
			resizable: false,
			sortable: false,
			renderCell: (params) =>
				isEditMode && params.row.isNew ? (
					<DateEditCell params={params} field='startDate' />
				) : params.row.startDate ? (
					dayjs.utc(params.row.startDate).format('MM/DD/YYYY')
				) : (
					''
				),
		},
		{
			field: 'endDate',
			headerName: 'End Date',
			headerAlign: 'center' as const,
			cellClassName: 'tableCell',
			flex: 2,
			resizable: false,
			sortable: false,
			renderCell: (params) => {
				const { endDate, isPastEndDateFromDb, hasEndDate, surchargeFeeNames } = params.row;

				if (isEditMode && !isPastEndDateFromDb && !(hasEndDate && surchargeFeeNames?.length > 0)) {
					return <DateEditCell params={params} field='endDate' />;
				}

				return endDate ? dayjs.utc(endDate).format('MM/DD/YYYY') : '';
			},
		},
		{
			field: 'surchargeFeeNames',
			headerName: 'Surcharge Fee Names',
			headerAlign: 'center' as const,
			cellClassName: 'tableCell',
			flex: 2,
			resizable: false,
			sortable: false,
			renderCell: (params) => (
				<Tooltip title={params.value || ''}>
					<span>{params.value}</span>
				</Tooltip>
			),
		},
	];

	if (isEditMode) {
		vehicleColumns.push({
			field: 'actions',
			type: 'actions' as const,
			getActions: (params) => {
				const startDate = params.row.startDate;
				const isFuture = startDate && dayjs.utc(startDate).isAfter(dayjs.utc().startOf('day'));
				if (params.row.isNew || isFuture) {
					return [
						<GridActionsCellItem
							key={params.id}
							icon={<DeleteOutlined />}
							label='Delete'
							onClick={handleDeleteVehicle(params.id)}
							data-cy='delete-icon-button'
						/>,
					];
				}
				return [];
			},
			cellClassName: 'actionCell',
			flex: 0.5,
			editable: false,
			resizable: false,
			sortable: false,
		});
	}

	return (
		<Grid className='pca__parkerDetail-vehicle' item xs={12} sx={{ mt: 1, mb: 1 }}>
			<h2>Vehicles {facilityIntegration === 'GET_MY_PARKING' && <span>*</span>}</h2>
			{!isEditMode && (
				<FormGroup>
					<FormControlLabel
						control={
							<Switch checked={showAllRows} onChange={(event) => setShowAllRows(event.target.checked)} />
						}
						label='Show Inactive Vehicles'
					/>
				</FormGroup>
			)}
			{(parkerId || newVehicles.length > 0) && (
				<DataGridPremium
					rows={isEditMode ? newVehicles : vehiclesToShow}
					columns={vehicleColumns}
					loading={vehicleLoading}
					getDetailPanelContent={getDetailPanelContent}
					getDetailPanelHeight={getDetailPanelHeight}
					slots={{
						detailPanelExpandIcon: KeyboardArrowRightIcon,
						detailPanelCollapseIcon: KeyboardArrowDownIcon,
					}}
					autoHeight
					hideFooter
					disableRowSelectionOnClick
					sx={{
						'& .MuiDataGrid-detailPanel': {
							backgroundColor: '#fafafa',
						},
					}}
				/>
			)}
			{isEditMode && !isOwnerPortal && (
				<Tooltip
					title={canAddMoreVehicles ? '' : addVehicleDisabledReason}
					disableHoverListener={canAddMoreVehicles}
				>
					<span>
						<Button
							variant='contained'
							disabled={!canAddMoreVehicles || vehicleLoading}
							onClick={handleAddVehicle}
							sx={{ mt: 1 }}
							size='small'
							data-cy='add-vehicle-button'
						>
							Add Vehicle
						</Button>
					</span>
				</Tooltip>
			)}
		</Grid>
	);
};
